import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Users,
  ClipboardList,
  Wallet,
  Settings2,
  LayoutDashboard,
  CheckCircle2,
  XCircle,
  PauseCircle,
  RefreshCw,
  Copy as CopyIcon,
  Download,
  AlertTriangle,
} from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { UsersTab } from "@/components/admin/users/UsersTab";
import { UserRound } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  getReferralAdminOverview,
  listAdminPartners,
  approvePartner,
  rejectPartner,
  suspendPartner,
  reactivatePartner,
  updateAdminPartner,
  regeneratePartnerCode,
  listAdminConversions,
  approveMaturedConversions,
  reverseConversion,
  listAdminPayouts,
  setPayoutStatus,
  getAdminReferralSettings,
  updateAdminReferralSettings,
} from "@/lib/referral/admin-referral.functions";

export const Route = createFileRoute("/_authenticated/admin/referrals")({
  validateSearch: (s: Record<string, unknown>) => ({ tab: typeof s.tab === "string" ? s.tab : "users" }),
  head: () => ({
    meta: [
      { title: "Админ — Потребители | FIXHEN" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AdminLayout>
      <ReferralsAdmin />
    </AdminLayout>
  ),
});

/* ---------------------------------- utils --------------------------------- */

const eur = (cents?: number | null) =>
  new Intl.NumberFormat("bg-BG", { style: "currency", currency: "EUR" }).format(
    Number(cents ?? 0) / 100,
  );
const bp = (b: number) => `${(b / 100).toFixed(b % 100 === 0 ? 0 : 2)}%`;
const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("bg-BG", { dateStyle: "medium", timeStyle: "short" }) : "—";

function StatusPill({ value }: { value: string }) {
  const map: Record<string, string> = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    suspended: "bg-orange-100 text-orange-800",
    rejected: "bg-rose-100 text-rose-800",
    paid: "bg-sky-100 text-sky-800",
    reversed: "bg-slate-200 text-slate-700",
    requested: "bg-amber-100 text-amber-800",
    processing: "bg-sky-100 text-sky-800",
    cancelled: "bg-rose-100 text-rose-800",
  };
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${map[value] ?? "bg-slate-100 text-slate-700"}`}
    >
      {value}
    </span>
  );
}

function TierPill({ value }: { value: string }) {
  const map: Record<string, string> = {
    silver: "bg-slate-200 text-slate-800",
    gold: "bg-yellow-100 text-yellow-800",
    platinum: "bg-violet-100 text-violet-800",
  };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${map[value] ?? ""}`}>
      {value}
    </span>
  );
}

/* --------------------------------- shell --------------------------------- */

const TAB_KEYS = ["users", "overview", "partners", "conversions", "payouts", "settings"];

function ReferralsAdmin() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const tab = TAB_KEYS.includes(search.tab) ? search.tab : "users";
  const setTab = (t: string) => navigate({ search: { tab: t }, replace: true });
  const fetchOverview = useServerFn(getReferralAdminOverview);
  const ov = useQuery({ queryKey: ["referrals-admin", "overview"], queryFn: () => fetchOverview() });
  const o: any = ov.data ?? {};
  const pendingPartners = Number(o.partners?.pending ?? 0);
  const requestedPayouts = Number(o.pendingPayouts?.count ?? 0);
  const Count = ({ n }: { n: number }) =>
    n > 0 ? <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">{n}</span> : null;
  return (
    <div className="p-3 sm:p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black tracking-tight">Потребители</h1>
          <p className="text-sm text-slate-500">Клиенти, фирмени и ДДС данни, и партньорската програма.</p>
        </div>
      </header>
      <Tabs value={tab} onValueChange={setTab}>
        <div className="overflow-x-auto">
          <TabsList className="h-auto flex w-max gap-1 p-1">
            <span className="px-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">Клиенти</span>
            <TabsTrigger value="users" className="gap-1.5"><UserRound className="h-3.5 w-3.5" />Потребители</TabsTrigger>
            <span className="mx-2 h-5 w-px bg-slate-300" aria-hidden />
            <span className="px-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">Партньорска програма</span>
            <TabsTrigger value="overview" className="gap-1.5"><LayoutDashboard className="h-3.5 w-3.5" />Обзор</TabsTrigger>
            <TabsTrigger value="partners" className="gap-1.5"><Users className="h-3.5 w-3.5" />Партньори<Count n={pendingPartners} /></TabsTrigger>
            <TabsTrigger value="conversions" className="gap-1.5"><ClipboardList className="h-3.5 w-3.5" />Конверсии</TabsTrigger>
            <TabsTrigger value="payouts" className="gap-1.5"><Wallet className="h-3.5 w-3.5" />Изплащания<Count n={requestedPayouts} /></TabsTrigger>
            <TabsTrigger value="settings" className="gap-1.5"><Settings2 className="h-3.5 w-3.5" />Настройки</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="overview" className="mt-4"><OverviewTab /></TabsContent>
        <TabsContent value="partners" className="mt-4"><PartnersTab /></TabsContent>
        <TabsContent value="conversions" className="mt-4"><ConversionsTab /></TabsContent>
        <TabsContent value="payouts" className="mt-4"><PayoutsTab /></TabsContent>
        <TabsContent value="settings" className="mt-4"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* -------------------------------- Overview -------------------------------- */

function OverviewTab() {
  const fetchOverview = useServerFn(getReferralAdminOverview);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin", "referrals", "overview"],
    queryFn: () => fetchOverview(),
    staleTime: 30_000,
  });

  const maxY = useMemo(
    () => Math.max(1, ...(data?.daily ?? []).map((d) => d.commission_cents)),
    [data],
  );

  if (isLoading || !data) return <Card className="p-6 text-sm text-slate-500">Зареждане…</Card>;

  const kpi = (label: string, value: string, hint?: string) => (
    <Card className="p-4">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-black tabular-nums">{value}</div>
      {hint ? <div className="text-xs text-slate-500 mt-1">{hint}</div> : null}
    </Card>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isFetching ? "animate-spin" : ""}`} />
          Обнови
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpi("Партньори", `${data.partners.total}`, `${data.partners.approved} одобрени · ${data.partners.pending} чакащи`)}
        {kpi("Комисионни (одобрени)", eur(data.commissions.approved), `Чакащи: ${eur(data.commissions.pending)}`)}
        {kpi("Изплатени", eur(data.commissions.paid), `Върнати: ${eur(data.commissions.reversed)}`)}
        {kpi("GMV (референ.)", eur(data.commissions.gmvCents), `${data.visits30d} посещения / 30 дни`)}
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-bold">Комисионни през последните 30 дни</div>
            <div className="text-xs text-slate-500">Ежедневни създадени комисионни</div>
          </div>
          <div className="text-xs text-slate-500">Макс: {eur(maxY)}</div>
        </div>
        <div className="flex items-end gap-1 h-32">
          {data.daily.map((d) => {
            const h = (d.commission_cents / maxY) * 100;
            return (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${eur(d.commission_cents)} · ${d.conversions} конв.`}>
                <div className="w-full bg-sky-500/80 rounded-t" style={{ height: `${Math.max(2, h)}%` }} />
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-bold">Чакащи изплащания</div>
            <div className="text-xs text-slate-500">{data.pendingPayouts.count} заявки · {eur(data.pendingPayouts.amount_cents)} нето</div>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* -------------------------------- Partners -------------------------------- */

function PartnersTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>("all");
  const [tier, setTier] = useState<string>("all");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<any | null>(null);

  const fetchPartners = useServerFn(listAdminPartners);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "referrals", "partners", { status, tier, q, offset }],
    queryFn: () =>
      fetchPartners({
        data: {
          status: status !== "all" ? (status as any) : undefined,
          tier: tier !== "all" ? (tier as any) : undefined,
          q: q || undefined,
          limit: 30,
          offset,
        },
      }),
    staleTime: 15_000,
  });

  const doApprove = useServerFn(approvePartner);
  const doReject = useServerFn(rejectPartner);
  const doSuspend = useServerFn(suspendPartner);
  const doReactivate = useServerFn(reactivatePartner);
  const doUpdate = useServerFn(updateAdminPartner);
  const doRegen = useServerFn(regeneratePartnerCode);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["admin", "referrals"] });

  const runAction = (fn: () => Promise<any>, msg: string) => {
    void toast.promise(
      fn().then(() => {
        invalidate();
        setSelected(null);
      }),
      { loading: "Обработва се…", success: msg, error: (e) => e?.message || "Грешка" },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Търси по име / код / компания…" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} className="max-w-xs" />
        <Select value={status} onValueChange={(v) => { setStatus(v); setOffset(0); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Статус" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Всички статуси</SelectItem>
            <SelectItem value="pending">Чакащи</SelectItem>
            <SelectItem value="approved">Одобрени</SelectItem>
            <SelectItem value="suspended">Спрени</SelectItem>
            <SelectItem value="rejected">Отхвърлени</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tier} onValueChange={(v) => { setTier(v); setOffset(0); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Ниво" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Всички нива</SelectItem>
            <SelectItem value="silver">Silver</SelectItem>
            <SelectItem value="gold">Gold</SelectItem>
            <SelectItem value="platinum">Platinum</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Партньор</TableHead>
              <TableHead>Код</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead>Ниво</TableHead>
              <TableHead className="text-right">Чакащи</TableHead>
              <TableHead className="text-right">Одобрени</TableHead>
              <TableHead className="text-right">Изплатени</TableHead>
              <TableHead>Създаден</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Зареждане…</TableCell></TableRow>
            ) : (data?.rows ?? []).length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Няма партньори.</TableCell></TableRow>
            ) : (
              (data?.rows ?? []).map((p: any) => (
                <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(p)}>
                  <TableCell>
                    <div className="font-semibold">{p.display_name}</div>
                    <div className="text-xs text-slate-500">{p.email ?? p.company ?? p.country}</div>
                  </TableCell>
                  <TableCell><code className="text-xs">{p.code}</code></TableCell>
                  <TableCell><StatusPill value={p.status} /></TableCell>
                  <TableCell><TierPill value={p.tier} /></TableCell>
                  <TableCell className="text-right tabular-nums">{eur(p.balance.pending_cents)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{eur(p.balance.approved_cents)}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-500">{eur(p.balance.paid_cents)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtDate(p.created_at)}</TableCell>
                  <TableCell className="text-right">→</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Pagination total={data?.total ?? 0} limit={30} offset={offset} onChange={setOffset} />

      {selected ? (
        <PartnerDialog
          partner={selected}
          onClose={() => setSelected(null)}
          onApprove={(id) => runAction(() => doApprove({ data: { id } }), "Одобрено")}
          onReject={(id, reason) => runAction(() => doReject({ data: { id, reason } }), "Отхвърлено")}
          onSuspend={(id, reason) => runAction(() => doSuspend({ data: { id, reason } }), "Спряно")}
          onReactivate={(id) => runAction(() => doReactivate({ data: { id } }), "Активирано")}
          onUpdate={(id, patch) => runAction(() => doUpdate({ data: { id, ...patch } }), "Записано")}
          onRegenerate={(id) => runAction(() => doRegen({ data: { id } }), "Нов код")}
        />
      ) : null}
    </div>
  );
}

function PartnerDialog({
  partner,
  onClose,
  onApprove,
  onReject,
  onSuspend,
  onReactivate,
  onUpdate,
  onRegenerate,
}: {
  partner: any;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onSuspend: (id: string, reason: string) => void;
  onReactivate: (id: string) => void;
  onUpdate: (id: string, patch: { manual_rate_override_bp?: number | null; notes?: string | null; tier?: "silver" | "gold" | "platinum" }) => void;
  onRegenerate: (id: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState<string>(partner.notes ?? "");
  const [override, setOverride] = useState<string>(
    partner.manual_rate_override_bp != null ? String(partner.manual_rate_override_bp) : "",
  );
  const [tier, setTier] = useState<string>(partner.tier);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {partner.display_name}
            <StatusPill value={partner.status} />
            <TierPill value={partner.tier} />
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <code className="text-xs">{partner.code}</code>
            <button
              type="button"
              className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1"
              onClick={() => { navigator.clipboard.writeText(partner.code); toast.success("Копирано"); }}
            >
              <CopyIcon className="h-3 w-3" /> копирай
            </button>
            <span className="text-xs text-slate-500">· {partner.email ?? partner.country}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Компания">{partner.company || "—"}</Field>
          <Field label="Държава">{partner.country || "—"}</Field>
          <Field label="Уебсайт">
            {partner.website ? (
              <a href={partner.website} className="text-sky-600 hover:underline" target="_blank" rel="noreferrer noopener">{partner.website}</a>
            ) : "—"}
          </Field>
          <Field label="Метод">{partner.payout_method || "—"}</Field>
          <Field label="Данъчен номер">{partner.tax_id || "—"}</Field>
          <Field label="Канали">
            <div className="flex flex-wrap gap-1">
              {(partner.promo_channels ?? []).map((c: string) => (
                <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>
              ))}
              {(!partner.promo_channels || !partner.promo_channels.length) && "—"}
            </div>
          </Field>
        </div>

        <div className="rounded-lg border p-3 bg-slate-50 text-sm">
          <div className="font-semibold mb-1">Аудитория</div>
          <p className="text-slate-700 whitespace-pre-wrap">{partner.audience_desc || "—"}</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <Kpi label="Чакащи" value={eur(partner.balance.pending_cents)} />
          <Kpi label="Одобрени" value={eur(partner.balance.approved_cents)} strong />
          <Kpi label="Изплатени" value={eur(partner.balance.paid_cents)} />
          <Kpi label="Върнати" value={eur(partner.balance.reversed_cents)} />
        </div>

        <div className="border rounded-lg p-3 space-y-2">
          <div className="text-sm font-semibold">Ръчни настройки</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Ниво (override)</Label>
              <Select value={tier} onValueChange={setTier}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="platinum">Platinum</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ръчен процент (bp; празно = auto)</Label>
              <Input inputMode="numeric" value={override} onChange={(e) => setOverride(e.target.value.replace(/[^0-9]/g, ""))} placeholder="напр. 1750 = 17.5%" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Вътрешни бележки</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => onRegenerate(partner.id)}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Нов код
            </Button>
            <Button
              size="sm"
              onClick={() =>
                onUpdate(partner.id, {
                  tier: tier as any,
                  manual_rate_override_bp: override === "" ? null : Number(override),
                  notes: notes.trim() || null,
                })
              }
            >
              Запис
            </Button>
          </div>
        </div>

        {(partner.status === "pending" || partner.status === "approved" || partner.status === "suspended") ? (
          <div className="border-t pt-3 space-y-2">
            <Label className="text-xs">Причина (при отхвърляне / спиране)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Опционално…" />
          </div>
        ) : null}

        <DialogFooter className="flex-wrap gap-2">
          {partner.status === "pending" ? (
            <>
              <Button variant="destructive" onClick={() => onReject(partner.id, reason)}>
                <XCircle className="h-4 w-4 mr-1" /> Отхвърли
              </Button>
              <Button onClick={() => onApprove(partner.id)}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Одобри
              </Button>
            </>
          ) : partner.status === "approved" ? (
            <Button variant="outline" onClick={() => onSuspend(partner.id, reason)}>
              <PauseCircle className="h-4 w-4 mr-1" /> Спри
            </Button>
          ) : partner.status === "suspended" ? (
            <Button onClick={() => onReactivate(partner.id)}>
              <CheckCircle2 className="h-4 w-4 mr-1" /> Активирай
            </Button>
          ) : partner.status === "rejected" ? (
            <Button onClick={() => onApprove(partner.id)}>
              <CheckCircle2 className="h-4 w-4 mr-1" /> Одобри
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function Kpi({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`tabular-nums ${strong ? "font-black" : ""}`}>{value}</div>
    </div>
  );
}

/* ------------------------------- Conversions ------------------------------ */

function ConversionsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [offset, setOffset] = useState(0);
  const [reverseId, setReverseId] = useState<string | null>(null);
  const [reverseReason, setReverseReason] = useState("");

  const fetchConversions = useServerFn(listAdminConversions);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "referrals", "conversions", { status, offset }],
    queryFn: () =>
      fetchConversions({
        data: {
          status: status !== "all" ? (status as any) : undefined,
          limit: 50,
          offset,
        },
      }),
    staleTime: 10_000,
  });

  const doApproveMatured = useServerFn(approveMaturedConversions);
  const doReverse = useServerFn(reverseConversion);
  const approveMut = useMutation({
    mutationFn: () => doApproveMatured({}),
    onSuccess: (r) => {
      toast.success(`Одобрени: ${r.approved}`);
      qc.invalidateQueries({ queryKey: ["admin", "referrals"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Грешка"),
  });

  const reverseMut = useMutation({
    mutationFn: (v: { id: string; reason: string }) => doReverse({ data: v }),
    onSuccess: () => {
      toast.success("Върнато");
      setReverseId(null);
      setReverseReason("");
      qc.invalidateQueries({ queryKey: ["admin", "referrals"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Грешка"),
  });

  const exportCsv = () => {
    const rows = data?.rows ?? [];
    const header = ["date", "code", "partner", "order", "order_status", "subtotal_eur", "rate_bp", "commission_eur", "status", "hold_until"];
    const csv = [
      header.join(","),
      ...rows.map((r: any) => [
        r.created_at,
        r.code_snapshot,
        JSON.stringify(r.partner?.display_name ?? ""),
        r.order?.order_number ?? "",
        r.order?.status ?? "",
        (r.subtotal_cents / 100).toFixed(2),
        r.commission_rate_bp,
        (r.commission_cents / 100).toFixed(2),
        r.status,
        r.hold_until ?? "",
      ].join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `conversions-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={status} onValueChange={(v) => { setStatus(v); setOffset(0); }}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Всички</SelectItem>
            <SelectItem value="pending">Чакащи</SelectItem>
            <SelectItem value="approved">Одобрени</SelectItem>
            <SelectItem value="paid">Изплатени</SelectItem>
            <SelectItem value="reversed">Върнати</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="h-3.5 w-3.5 mr-1" /> CSV
        </Button>
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={() => approveMut.mutate()}
          disabled={approveMut.isPending}
        >
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
          Одобри узрелите
        </Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Дата</TableHead>
              <TableHead>Партньор</TableHead>
              <TableHead>Поръчка</TableHead>
              <TableHead className="text-right">Subtotal</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Комисионна</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead>Hold до</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Зареждане…</TableCell></TableRow>
            ) : (data?.rows ?? []).length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Няма записи.</TableCell></TableRow>
            ) : (
              (data?.rows ?? []).map((c: any) => (
                <TableRow key={c.id}>
                  <TableCell className="text-xs">{fmtDate(c.created_at)}</TableCell>
                  <TableCell>
                    <div className="text-sm font-semibold">{c.partner?.display_name ?? "—"}</div>
                    <code className="text-[10px] text-slate-500">{c.code_snapshot}</code>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{c.order?.order_number ?? "—"}</div>
                    <div className="text-[10px] text-slate-500">{c.order?.status ?? ""}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{eur(c.subtotal_cents)}</TableCell>
                  <TableCell className="text-right tabular-nums">{bp(c.commission_rate_bp)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{eur(c.commission_cents)}</TableCell>
                  <TableCell><StatusPill value={c.status} /></TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtDate(c.hold_until)}</TableCell>
                  <TableCell className="text-right">
                    {(c.status === "pending" || c.status === "approved") ? (
                      <Button size="sm" variant="ghost" onClick={() => { setReverseId(c.id); setReverseReason(""); }}>
                        <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Pagination total={data?.total ?? 0} limit={50} offset={offset} onChange={setOffset} />

      <Dialog open={!!reverseId} onOpenChange={(o) => !o && setReverseId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Върни комисионна</DialogTitle>
            <DialogDescription>
              Партньорът вече няма да получи тази комисионна. Причината се записва.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} placeholder="Причина за връщане…" rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseId(null)}>Отказ</Button>
            <Button
              variant="destructive"
              disabled={!reverseReason.trim() || reverseMut.isPending}
              onClick={() => reverseMut.mutate({ id: reverseId!, reason: reverseReason.trim() })}
            >
              Върни
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* --------------------------------- Payouts -------------------------------- */

function PayoutsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<any | null>(null);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");

  const fetchPayouts = useServerFn(listAdminPayouts);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "referrals", "payouts", { status, offset }],
    queryFn: () =>
      fetchPayouts({
        data: {
          status: status !== "all" ? (status as any) : undefined,
          limit: 50,
          offset,
        },
      }),
    staleTime: 10_000,
  });

  const doSetStatus = useServerFn(setPayoutStatus);
  const setStatusMut = useMutation({
    mutationFn: (v: { id: string; status: any; reference?: string | null; admin_note?: string | null }) =>
      doSetStatus({ data: v }),
    onSuccess: () => {
      toast.success("Обновено");
      setEditing(null); setReference(""); setNote("");
      qc.invalidateQueries({ queryKey: ["admin", "referrals"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Грешка"),
  });

  const exportCsv = () => {
    const rows = data?.rows ?? [];
    const header = ["requested_at", "partner", "code", "method", "amount_eur", "fee_eur", "net_eur", "status", "reference", "paid_at"];
    const csv = [
      header.join(","),
      ...rows.map((r: any) => [
        r.requested_at,
        JSON.stringify(r.partner?.display_name ?? ""),
        r.partner?.code ?? "",
        r.method ?? "",
        (r.amount_cents / 100).toFixed(2),
        (r.fee_cents / 100).toFixed(2),
        (r.net_cents / 100).toFixed(2),
        r.status,
        JSON.stringify(r.reference ?? ""),
        r.paid_at ?? "",
      ].join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payouts-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={status} onValueChange={(v) => { setStatus(v); setOffset(0); }}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Всички</SelectItem>
            <SelectItem value="requested">Заявени</SelectItem>
            <SelectItem value="processing">В обработка</SelectItem>
            <SelectItem value="paid">Изплатени</SelectItem>
            <SelectItem value="cancelled">Отказани</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="h-3.5 w-3.5 mr-1" /> CSV
        </Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Заявено</TableHead>
              <TableHead>Партньор</TableHead>
              <TableHead>Метод</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Fee</TableHead>
              <TableHead className="text-right">Net</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Зареждане…</TableCell></TableRow>
            ) : (data?.rows ?? []).length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Няма изплащания.</TableCell></TableRow>
            ) : (
              (data?.rows ?? []).map((p: any) => (
                <TableRow key={p.id}>
                  <TableCell className="text-xs">{fmtDate(p.requested_at)}</TableCell>
                  <TableCell>
                    <div className="text-sm font-semibold">{p.partner?.display_name ?? "—"}</div>
                    <code className="text-[10px] text-slate-500">{p.partner?.code}</code>
                  </TableCell>
                  <TableCell className="text-xs uppercase">{p.method ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{eur(p.amount_cents)}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-500">{eur(p.fee_cents)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{eur(p.net_cents)}</TableCell>
                  <TableCell><StatusPill value={p.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(p); setReference(p.reference ?? ""); setNote(p.admin_note ?? ""); }}>
                      Обработи
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Pagination total={data?.total ?? 0} limit={50} offset={offset} onChange={setOffset} />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Обработка на изплащане</DialogTitle>
            <DialogDescription>
              {editing?.partner?.display_name} · {eur(editing?.net_cents)} нето
            </DialogDescription>
          </DialogHeader>
          {editing?.partner?.payout_details ? (
            <div className="rounded-md bg-slate-50 p-3 text-xs">
              <div className="font-semibold mb-1">Реквизити ({editing.partner.payout_method})</div>
              <pre className="whitespace-pre-wrap break-words">
                {JSON.stringify(editing.partner.payout_details, null, 2)}
              </pre>
            </div>
          ) : null}
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Референция (TX ID, IBAN batch…)</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Вътрешна бележка</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
            </div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setStatusMut.mutate({ id: editing!.id, status: "cancelled", reference: reference || null, admin_note: note || null })} disabled={setStatusMut.isPending}>
              Откажи
            </Button>
            <Button variant="secondary" onClick={() => setStatusMut.mutate({ id: editing!.id, status: "processing", reference: reference || null, admin_note: note || null })} disabled={setStatusMut.isPending}>
              В обработка
            </Button>
            <Button onClick={() => setStatusMut.mutate({ id: editing!.id, status: "paid", reference: reference || null, admin_note: note || null })} disabled={setStatusMut.isPending}>
              Маркирай платено
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------- Settings -------------------------------- */

function SettingsTab() {
  const qc = useQueryClient();
  const fetchSettings = useServerFn(getAdminReferralSettings);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "referrals", "settings"],
    queryFn: () => fetchSettings(),
    staleTime: 30_000,
  });

  const [form, setForm] = useState<any>(null);
  const [bumpTerms, setBumpTerms] = useState(false);

  const s = data?.settings;
  const current = form ?? s;

  const doUpdate = useServerFn(updateAdminReferralSettings);
  const mut = useMutation({
    mutationFn: (v: any) => doUpdate({ data: v }),
    onSuccess: () => {
      toast.success("Записано");
      setForm(null);
      setBumpTerms(false);
      qc.invalidateQueries({ queryKey: ["admin", "referrals"] });
      qc.invalidateQueries({ queryKey: ["referral", "public-settings"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Грешка"),
  });

  if (isLoading || !current) return <Card className="p-6 text-sm text-slate-500">Зареждане…</Card>;

  const bind = (key: string) => ({
    value: String(current[key] ?? ""),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...current, [key]: e.target.value === "" ? 0 : Number(e.target.value.replace(/[^0-9]/g, "")) }),
  });

  return (
    <div className="max-w-3xl space-y-4">
      <Card className="p-4 space-y-4">
        <div>
          <div className="text-sm font-black">Проценти по нива (basis points)</div>
          <p className="text-xs text-slate-500">100 bp = 1%. Пример: 1250 bp = 12.5%.</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Silver bp</Label><Input inputMode="numeric" {...bind("tier_silver_bp")} /></div>
          <div><Label className="text-xs">Gold bp</Label><Input inputMode="numeric" {...bind("tier_gold_bp")} /></div>
          <div><Label className="text-xs">Platinum bp</Label><Input inputMode="numeric" {...bind("tier_platinum_bp")} /></div>
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <div>
          <div className="text-sm font-black">Прагове за нива (в центове)</div>
          <p className="text-xs text-slate-500">Base на верифицирани приходи lifetime.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Gold threshold (cents)</Label><Input inputMode="numeric" {...bind("gold_threshold_cents")} /></div>
          <div><Label className="text-xs">Platinum threshold (cents)</Label><Input inputMode="numeric" {...bind("platinum_threshold_cents")} /></div>
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <div className="text-sm font-black">Cookie / hold / payout</div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Cookie дни</Label><Input inputMode="numeric" {...bind("cookie_days")} /></div>
          <div><Label className="text-xs">Hold дни</Label><Input inputMode="numeric" {...bind("hold_days")} /></div>
          <div><Label className="text-xs">Min payout (cents)</Label><Input inputMode="numeric" {...bind("min_payout_cents")} /></div>
          <div><Label className="text-xs">Payout fee (cents)</Label><Input inputMode="numeric" {...bind("payout_fee_cents")} /></div>
        </div>
      </Card>

      <Card className="p-4 space-y-3">
        <div>
          <div className="text-sm font-black">Условия</div>
          <p className="text-xs text-slate-500">Текуща версия: <strong>{current.terms_version}</strong>. При bump всеки партньор ще види банер за повторно приемане.</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={bumpTerms} onChange={(e) => setBumpTerms(e.target.checked)} />
          Bump версията на условията (+1)
        </label>
      </Card>

      <div className="flex justify-end gap-2">
        {form ? (
          <Button variant="outline" onClick={() => { setForm(null); setBumpTerms(false); }}>Отказ</Button>
        ) : null}
        <Button
          disabled={mut.isPending || (!form && !bumpTerms)}
          onClick={() => {
            const payload: any = { bump_terms: bumpTerms };
            if (form) {
              for (const k of Object.keys(form)) {
                if (typeof form[k] === "number") payload[k] = form[k];
              }
            }
            mut.mutate(payload);
          }}
        >
          Запис
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------- Pagination ------------------------------- */

function Pagination({ total, limit, offset, onChange }: { total: number; limit: number; offset: number; onChange: (n: number) => void }) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(total, offset + limit);
  const canPrev = offset > 0;
  const canNext = offset + limit < total;
  return (
    <div className="flex items-center justify-between text-xs text-slate-500">
      <div>{from}–{to} от {total}</div>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" disabled={!canPrev} onClick={() => onChange(Math.max(0, offset - limit))}>Предишни</Button>
        <Button size="sm" variant="outline" disabled={!canNext} onClick={() => onChange(offset + limit)}>Следващи</Button>
      </div>
    </div>
  );
}
